import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Stack,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormat } from "../../i18n/format";
import { translateKey, type Translate } from "../../i18n/translate";
import {
  eventAt,
  GUIDE_DEFAULT_LANGUAGE,
  type ChannelGuide,
  type GuideEvent,
  type GuideGap,
  type InvalidGuideEvent,
} from "@shared/epg-guide";
import { foyerApi, type ChannelSchedule } from "../../http/foyer";
import { epgErrorText } from "./findings";
import { SectionHead, SoftChip } from "./editorBits";
import EpgEventDetailDialog from "./EpgEventDetailDialog";
import { clock, day, span, stamp } from "./scheduleFormat";
import { tokens } from "../../theme";

/**
 * A channel's schedule, where the mapping that produces it is set (`BE-27`).
 *
 * ## Why this is a panel in an editor and not a guide page
 *
 * Nobody authors EPG. It is read-only data ingested from Simply TV three times
 * a day and Foyer will never write a row of it, so the only job a CMS schedule
 * surface has is **diagnosis**: did this channel's mapping actually produce a
 * schedule, and does it look right? That question belongs next to the thing
 * that decides the answer - the channel's `epg-simply` `alternativeIds` entry
 * (`BE-22`) - and not on a separate page an operator has to think to visit.
 *
 * ## Three things this panel is careful about
 *
 * 1. **"On now" is computed here, from this browser's clock.** The server ships
 *    no `live` flag on purpose: the read behind it is cached in ten-minute bins
 *    (see `api/distributionEpg.ts`), so a baked flag would be wrong within
 *    seconds of being cached. {@link eventAt} is the one function every surface
 *    calls, so a TV guide and this panel cannot disagree at a junction.
 *
 * 2. **A hole is drawn, not left blank.** An unmapped channel and a channel
 *    with a genuinely empty guide are the SAME answer upstream - `200` with no
 *    rows, because `GET /channels/:assetId/epgevents` never checks the channel
 *    exists. So `gaps[]` is rendered as visible missing time, and a window-wide
 *    gap is the strongest signal this surface has that the mapping is wrong.
 *
 * 3. **A dead catalogue is an error with a Retry, never an empty schedule.**
 *    Same rule `BE-15` applied to the lists: "the catalogue is down" must not
 *    read as "this channel has no guide".
 *
 * ## Why there is no week picker
 *
 * Ingest keeps only `[yesterday 00:00, dayAfterTomorrow 00:00)`, so the data
 * ends about two days out. A control offering a week would be a lie the UI
 * tells about data that does not exist. `EPG_MAX_WINDOW_DAYS = 14` is a
 * bug-catching ceiling, not a product horizon.
 */

/** The default window. Short, because the data only reaches ~2 days out. */
export const SCHEDULE_WINDOW_HOURS = 24;

/** How often "on now" is recomputed. */
const TICK_MS = 30_000;

/* ------------------------------------------------------------------ *
 * The timeline
 * ------------------------------------------------------------------ */

export type TimelineEntry =
  | { kind: "event"; at: number; event: GuideEvent }
  | { kind: "gap"; at: number; gap: GuideGap };

/**
 * Events and gaps, interleaved in one chronological list.
 *
 * Merged rather than drawn as two lists because a hole is only legible in the
 * place it happens: "no schedule between 03:00 and 06:00" underneath the 03:00
 * programme says something a separate "Gaps" box never can.
 *
 * A gap ties with the event that follows it only if it is zero-length, which
 * the server has already filtered out (`GUIDE_GAP_MIN_SECONDS`); the tie-break
 * puts the gap first anyway so the order is total.
 */
export function scheduleTimeline(
  events: readonly GuideEvent[],
  gaps: readonly GuideGap[]
): TimelineEntry[] {
  const entries: TimelineEntry[] = [
    ...events.map(
      (event): TimelineEntry => ({
        kind: "event",
        at: Date.parse(event.startScheduled),
        event,
      })
    ),
    ...gaps.map(
      (gap): TimelineEntry => ({ kind: "gap", at: Date.parse(gap.from), gap })
    ),
  ];
  const rank = (entry: TimelineEntry) => (entry.kind === "gap" ? 0 : 1);
  return entries.sort((a, b) => a.at - b.at || rank(a) - rank(b));
}

/* ------------------------------------------------------------------ *
 * Formatting
 * ------------------------------------------------------------------ */

function ago(t: Translate, seconds: number): string {
  const k = (name: string, params?: Record<string, number>) =>
    translateKey(t, `channels:channelSchedulePreview.ago.${name}`, params);
  if (seconds < 60) return k("justNow");
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return k("minutes", { count: minutes });
  const hours = Math.round(minutes / 60);
  return k("hours", { count: hours });
}

/* ------------------------------------------------------------------ *
 * Rows
 * ------------------------------------------------------------------ */

function EventRow({
  event,
  live,
  guideLanguage,
  onOpen,
}: {
  event: GuideEvent;
  live: boolean;
  /** The language the panel asked for (`data.language`) - compared against
   *  `event.titleLanguage` to tell "this is the language you asked for" from
   *  "ingest never wrote that language, this is a fallback". */
  guideLanguage: string;
  /** Opens the artwork/metadata detail for this event. Every list of this
   *  kind in the CMS opens on the row (see `distribution-cms-surface`'s "a
   *  row opens the asset"), so a schedule row does the same rather than
   *  hiding it behind a menu nobody thinks to open. */
  onOpen: () => void;
}) {
  const { t } = useTranslation("channels");
  const fmt = useFormat();
  return (
    <Stack
      direction="row"
      spacing={2}
      data-testid={`schedule-event-${event.epgEventId}`}
      data-live={live ? "true" : undefined}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 1.5,
        bgcolor: live ? tokens.brandTintSoft : "transparent",
        cursor: "pointer",
        "&:hover": { bgcolor: tokens.brandTintSoft },
      }}
    >
      <Box sx={{ flex: "0 0 auto", width: 96 }}>
        <Typography
          variant="bodySm"
          sx={{
            fontWeight: "fontWeightBold",
            color: live ? tokens.brand : tokens.ink,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {clock(fmt, event.startScheduled)}
        </Typography>
        <Typography variant="micro" sx={{ color: tokens.inkMuted }}>
          {span(t, event.durationSeconds)}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            variant="bodySm"
            sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
            data-testid={`schedule-title-${event.epgEventId}`}
          >
            {event.title || t("channelSchedulePreview.untitledProgramme")}
          </Typography>
          {live && (
            <Box
              data-testid="schedule-on-now"
              sx={{
                px: 1,
                py: 0.125,
                borderRadius: 999,
                bgcolor: tokens.brand,
                color: "#fff",
                typography: "micro",
                fontWeight: "fontWeightBold",
                letterSpacing: 0.4,
              }}
            >
              {t("channelSchedulePreview.onNow")}
            </Box>
          )}
        </Stack>
        {event.synopsis && (
          <Typography
            variant="meta"
            sx={{
              color: tokens.inkMuted,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {event.synopsis}
          </Typography>
        )}
        {/* Which language answered, or that nothing did and this is the
            programme's own original title. Cheap, and it is the difference
            between "the guide is in the wrong language" and "there is no
            localised row for it". Two distinct cases, worded differently on
            purpose: a same-asset fallback to another ingested language still
            means ingest wrote real text, which "no localised row" would deny. */}
        {event.titleLanguage === null && event.title && (
          <Typography variant="micro" sx={{ color: tokens.inkFaint }}>
            {t("channelSchedulePreview.originalTitle")}
          </Typography>
        )}
        {event.titleLanguage !== null &&
          event.titleLanguage !== guideLanguage && (
            <Typography variant="micro" sx={{ color: tokens.inkFaint }}>
              {t("channelSchedulePreview.shownIn", {
                shown: event.titleLanguage.toUpperCase(),
                wanted: guideLanguage.toUpperCase(),
              })}
            </Typography>
          )}
      </Box>
      <Typography
        variant="micro"
        sx={{
          flex: "0 0 auto",
          color: tokens.inkFaint,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {clock(fmt, event.endScheduled)}
      </Typography>
    </Stack>
  );
}

function GapRow({ gap }: { gap: GuideGap }) {
  const { t } = useTranslation("channels");
  const fmt = useFormat();
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      data-testid="schedule-gap"
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        border: `1px dashed ${tokens.warning}`,
        bgcolor: tokens.warningTint,
      }}
    >
      <Typography
        variant="meta"
        sx={{
          flex: "0 0 auto",
          width: 96,
          fontWeight: "fontWeightBold",
          color: tokens.warning,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {clock(fmt, gap.from)}
      </Typography>
      <Typography variant="meta" sx={{ color: tokens.ink }}>
        {t("channelSchedulePreview.gap", {
          span: span(t, gap.seconds),
          from: clock(fmt, gap.from),
          to: clock(fmt, gap.to),
        })}
      </Typography>
    </Stack>
  );
}

function InvalidRow({ row }: { row: InvalidGuideEvent }) {
  const { t } = useTranslation("channels");
  return (
    <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
      {t(
        row.reason === "not_after_start"
          ? "channelSchedulePreview.invalidNotAfterStart"
          : "channelSchedulePreview.invalidBadTimestamp",
        {
          id: row.epgEventId,
          start: String(row.startScheduled),
          end: String(row.endScheduled),
        }
      )}
    </Typography>
  );
}

/* ------------------------------------------------------------------ *
 * The panel
 * ------------------------------------------------------------------ */

function nextWindow(): { from: string; to: string } {
  const now = Date.now();
  return {
    from: new Date(now).toISOString(),
    to: new Date(now + SCHEDULE_WINDOW_HOURS * 3_600_000).toISOString(),
  };
}

export interface ChannelSchedulePreviewProps {
  /** The channel's `assetId`. A channel being created has none, so the caller
   *  simply does not render this - there is nothing to read yet. */
  assetId: string;
}

export default function ChannelSchedulePreview({
  assetId,
}: ChannelSchedulePreviewProps) {
  const { t } = useTranslation(["channels", "common"]);
  const fmt = useFormat();
  const [range, setRange] = useState(nextWindow);
  const [data, setData] = useState<ChannelSchedule | null>(null);
  const [readAt, setReadAt] = useState(() => Date.now());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSuperseded, setShowSuperseded] = useState(false);
  const [detailEvent, setDetailEvent] = useState<GuideEvent | null>(null);

  /** Ticks so "on now" follows the clock rather than the fetch. */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const load = useCallback(
    async (window: { from: string; to: string }) => {
      setLoading(true);
      setError(null);
      try {
        const answer = await foyerApi.getChannelSchedule(assetId, window);
        setData(answer);
        setReadAt(Date.now());
      } catch (e) {
        const err = e as {
          code?: string;
          message: string;
          body?: { detail?: string };
        };
        // Never an empty schedule on failure. A 502 here means the catalogue is
        // unwell, and drawing "no programmes" would blame the channel for it.
        setData(null);
        setError(
          epgErrorText(t, {
            code: err.code,
            detail: err.body?.detail,
            message: err.message,
          })
        );
      } finally {
        setLoading(false);
      }
    },
    [assetId, t]
  );

  useEffect(() => {
    void load(range);
  }, [load, range]);

  const guide: ChannelGuide | null = data?.channels?.[0] ?? null;
  const timeline = useMemo(
    () => (guide ? scheduleTimeline(guide.events, guide.gaps) : []),
    [guide]
  );
  const onNow = useMemo(
    () => (guide ? eventAt(guide.events, now) : null),
    [guide, now]
  );

  /** The server's age at the moment it answered, plus how long ago that was. */
  const freshness = data
    ? ago(t, data.cache.ageSeconds + Math.max(0, (now - readAt) / 1000))
    : null;

  const summary = guide
    ? [
        t("channelSchedulePreview.programmes", {
          count: guide.events.length,
        }),
        guide.gaps.length > 0
          ? t("channelSchedulePreview.gaps", { count: guide.gaps.length })
          : t("channelSchedulePreview.noGaps"),
        onNow
          ? t("channelSchedulePreview.onNowTitle", { title: onNow.title })
          : t("channelSchedulePreview.nothingOn"),
      ].join(" · ")
    : null;

  return (
    <Box data-testid="channel-schedule">
      <SectionHead
        title={t("channelSchedulePreview.sectionTitle")}
        subtitle={t("channelSchedulePreview.sectionSubtitle")}
        aside={
          <>
            {freshness && (
              <SoftChip
                label={t("channelSchedulePreview.readAgo", { freshness })}
                title={t("channelSchedulePreview.cachedHint", {
                  minutes: Math.round((data?.cache.binSeconds ?? 0) / 60),
                })}
              />
            )}
            <Button
              size="small"
              startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
              onClick={() => setRange(nextWindow())}
              disabled={loading}
              data-testid="schedule-refresh"
              sx={{
                minHeight: 0,
                fontSize: 12,
                fontWeight: "fontWeightSemibold",
              }}
            >
              {t("common:buttons.refresh")}
            </Button>
          </>
        }
      />

      {/* Both instants, always stated. This data set punishes "today": ingest
          computes its horizon from a server-local midnight in a UTC container,
          so the only honest window is the two moments the request named. */}
      <Typography
        variant="meta"
        sx={{ color: tokens.inkMuted, mb: 1.5 }}
        data-testid="schedule-window"
      >
        {t("channelSchedulePreview.window", {
          hours: SCHEDULE_WINDOW_HOURS,
          from: stamp(fmt, range.from),
          to: stamp(fmt, range.to),
        })}
        {data
          ? t("channelSchedulePreview.titlesIn", {
              language: data.language.toUpperCase(),
            })
          : ""}
      </Typography>

      {error && (
        <Alert
          severity="error"
          data-testid="schedule-error"
          action={
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => void load(range)}
            >
              {t("channelSchedulePreview.retry")}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading && !data && !error && (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      )}

      {guide && !error && (
        <>
          {guide.events.length === 0 ? (
            /* The whole point of the panel. Upstream answers 200 with no rows
               for a channel that is not mapped AND for one that simply has no
               schedule, so this says both out loud rather than drawing nothing
               and letting the operator guess which it was. */
            <Alert severity="warning" data-testid="schedule-empty">
              <Typography
                variant="bodySm"
                sx={{ fontWeight: "fontWeightBold", mb: 0.5 }}
              >
                {t("channelSchedulePreview.emptyTitle")}
              </Typography>
              <Typography variant="meta">
                {t("channelSchedulePreview.emptyBody", {
                  span:
                    guide.gaps.length > 0
                      ? span(t, guide.gaps[0].seconds)
                      : t("channelSchedulePreview.windowHours", {
                          count: SCHEDULE_WINDOW_HOURS,
                        }),
                  from: stamp(fmt, range.from),
                })}
              </Typography>
            </Alert>
          ) : (
            <>
              <Typography
                variant="meta"
                sx={{ color: tokens.inkMuted, mb: 1 }}
                data-testid="schedule-summary"
              >
                {summary}
              </Typography>
              <Box
                sx={{
                  border: `1px solid ${tokens.divider}`,
                  borderRadius: 2,
                  maxHeight: 460,
                  overflowY: "auto",
                  p: 1,
                }}
              >
                <Stack spacing={0.5}>
                  {timeline.map((entry, index) => {
                    const previous = timeline[index - 1];
                    const iso =
                      entry.kind === "event"
                        ? entry.event.startScheduled
                        : entry.gap.from;
                    const newDay =
                      index === 0 ||
                      (previous &&
                        day(
                          fmt,
                          previous.kind === "event"
                            ? previous.event.startScheduled
                            : previous.gap.from
                        ) !== day(fmt, iso));
                    return (
                      <Box
                        key={
                          entry.kind === "event"
                            ? `e${entry.event.epgEventId}`
                            : `g${entry.gap.from}`
                        }
                      >
                        {newDay && (
                          <Typography
                            variant="micro"
                            sx={{
                              typography: "eyebrow",
                              color: tokens.inkFaint,
                              px: 1.5,
                              pt: index === 0 ? 0.5 : 1.5,
                              pb: 0.5,
                            }}
                          >
                            {day(fmt, iso)}
                          </Typography>
                        )}
                        {entry.kind === "event" ? (
                          <EventRow
                            event={entry.event}
                            live={onNow?.epgEventId === entry.event.epgEventId}
                            guideLanguage={
                              data?.language ?? GUIDE_DEFAULT_LANGUAGE
                            }
                            onOpen={() => setDetailEvent(entry.event)}
                          />
                        ) : (
                          <GapRow gap={entry.gap} />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </>
          )}

          {/* Normal data, not corruption: an EPG event is immutable upstream,
              so a rescheduled programme is a SECOND row rather than an edit,
              and the older one survives until the purge. Out of the main flow,
              because it is noise - but reachable, because an operator chasing
              "the guide showed the wrong film" needs to see it. */}
          {guide.superseded.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Button
                size="small"
                onClick={() => setShowSuperseded((v) => !v)}
                endIcon={
                  showSuperseded ? (
                    <ExpandLessIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 16 }} />
                  )
                }
                data-testid="schedule-superseded-toggle"
                sx={{
                  minHeight: 0,
                  px: 0,
                  fontSize: 12,
                  fontWeight: "fontWeightSemibold",
                }}
              >
                {t("channelSchedulePreview.replacedAirings", {
                  count: guide.superseded.length,
                })}
              </Button>
              <Collapse in={showSuperseded}>
                <Typography
                  variant="micro"
                  sx={{ color: tokens.inkMuted, mb: 1 }}
                >
                  {t("channelSchedulePreview.supersededNote")}
                </Typography>
                <Stack spacing={0.5} data-testid="schedule-superseded">
                  {guide.superseded.map((row) => (
                    <Typography
                      variant="meta"
                      key={row.epgEventId}
                      sx={{ color: tokens.inkMuted }}
                    >
                      {t("channelSchedulePreview.supersededRow", {
                        start: stamp(fmt, row.startScheduled),
                        end: clock(fmt, row.endScheduled),
                        title:
                          row.title || t("channelSchedulePreview.untitled"),
                        by: row.supersededBy,
                        ingested: stamp(fmt, row.ingestedAt),
                      })}
                    </Typography>
                  ))}
                </Stack>
              </Collapse>
            </Box>
          )}

          {guide.invalid.length > 0 && (
            <Alert
              severity="warning"
              sx={{ mt: 1.5 }}
              data-testid="schedule-invalid"
            >
              <Typography
                variant="bodySm"
                sx={{ fontWeight: "fontWeightBold", mb: 0.5 }}
              >
                {t("channelSchedulePreview.invalidCount", {
                  count: guide.invalid.length,
                })}
              </Typography>
              <Stack spacing={0.25}>
                {guide.invalid.map((row) => (
                  <InvalidRow key={row.epgEventId} row={row} />
                ))}
              </Stack>
            </Alert>
          )}

          {/* Should always be empty - upstream filters by channel. It is drawn
              when it is not, because this API has already been caught twice
              answering for a row the path did not ask about. */}
          {data && data.foreignChannelAssetIds.length > 0 && (
            <Alert
              severity="warning"
              sx={{ mt: 1.5 }}
              data-testid="schedule-foreign"
            >
              {t("channelSchedulePreview.foreign", {
                ids: data.foreignChannelAssetIds.join(", "),
              })}
            </Alert>
          )}
        </>
      )}

      {detailEvent && (
        <EpgEventDetailDialog
          channelAssetId={detailEvent.channelAssetId}
          fallback={detailEvent}
          language={data?.language ?? GUIDE_DEFAULT_LANGUAGE}
          onClose={() => setDetailEvent(null)}
        />
      )}
    </Box>
  );
}
